<template>
  <div>
    <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        loading-text="Loading... Please wait"
        :headers="translatedHeaders"
        :items="users"
        :footer-props="footerProps"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            :label="$t('Search')"
            class="mx-4"
            clearable
            @input="onSearchInput"
          ></v-text-field>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-1">
            <v-avatar size="36" class="me-2 text-caption white--text font-weight-bold primary elevation-1 flex-shrink-0">
              <v-img
                v-if="getAvatarUrl(item) && !failedAvatars[item.id]"
                :src="getAvatarUrl(item)"
                @error="$set(failedAvatars, item.id, true)"
              ></v-img>
              <span v-else>{{ getInitials(item.name) }}</span>
            </v-avatar>
            <div class="d-flex flex-column justify-center" style="min-width: 0;">
              <span
                v-if="userType === 'drivers'"
                class="driver-name-text font-weight-bold text-decoration-underline text-truncate"
                style="cursor: pointer; color: #111827 !important; opacity: 1 !important;"
                @click="$emit('view-driver-details', item)"
              >
                {{ item.name }}
              </span>
              <span v-else class="text-truncate" style="color: #111827 !important; opacity: 1 !important;">{{ item.name }}</span>

              <v-tooltip v-if="isDriverItem(item)" bottom left open-on-hover>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    class="d-inline-flex align-center mt-1"
                    style="font-size: 11px; line-height: 1; width: fit-content; user-select: none;"
                  >
                    <v-icon
                      x-small
                      :color="hasRating(item) ? '#f59e0b' : '#9ca3af'"
                      class="me-1"
                      style="font-size: 12px; width: 12px; height: 12px;"
                    >
                      mdi-star
                    </v-icon>
                    <span
                      :style="{
                        color: hasRating(item) ? '#d97706' : '#6b7280',
                        fontWeight: hasRating(item) ? '600' : '400'
                      }"
                    >
                      {{ formatRating(item) }}
                    </span>
                  </div>
                </template>
                <span>{{ getRatingTooltip(item) }}</span>
              </v-tooltip>

              <div
                v-if="isDriverItem(item) && hasMonthlyDistance(item)"
                class="d-inline-flex align-center mt-1 ms-2"
                style="font-size: 11px; line-height: 1; width: fit-content; user-select: none;"
              >
                <v-icon
                  x-small
                  color="#6366f1"
                  class="me-1"
                  style="font-size: 12px; width: 12px; height: 12px;"
                >
                  mdi-bus
                </v-icon>
                <span style="color: #4f46e5; font-weight: 600;">
                  {{ formatMonthlyDistance(item) }}
                </span>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:item.phone_number="{ item }">
          <span>{{ getPhoneNumber(item) }}</span>
        </template>

        <template v-slot:item.status_id="{ item }">
          <v-chip
            :color="getStatusColor(item.status_id)"
            dark
          >
            {{ $t(getStatusValue(item.status_id)) }}
          </v-chip>
        </template>

        <template v-slot:item.working_status="{ item }">
          <v-chip
            small
            label
            :color="item.working_status === 'inactive' ? 'error' : 'success'"
            text-color="white"
          >
            {{ item.working_status === 'inactive' ? $t('working_status_inactive') : $t('working_status_active') }}
          </v-chip>
        </template>

        <template v-if="userType === 'drivers'" v-slot:item.bus="{ item }">
          <v-chip
            dark
            @click="$emit('assignBus', item)"
            :color="getBusAssignmentColor(item.bus)"
          >
            {{ item.bus != null? item.bus.license : 'No bus' }}
          </v-chip>
        </template>

        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>

        <template v-slot:item.rejected_documents="{ item }">
          <div v-if="getRejectedDocs(item).length > 0">
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-flex align-center flex-wrap" style="cursor: pointer;">
                  <v-chip
                    v-for="(docName, idx) in getRejectedDocsPreview(item)"
                    :key="idx"
                    x-small
                    color="error"
                    outlined
                    class="me-1 mb-1 font-weight-medium"
                  >
                    {{ docName }}
                  </v-chip>
                  <v-chip
                    v-if="getRejectedDocsMoreCount(item) > 0"
                    x-small
                    color="grey darken-1"
                    outlined
                    class="mb-1"
                  >
                    +{{ getRejectedDocsMoreCount(item) }} أخرى
                  </v-chip>
                </div>
              </template>
              <span>{{ $t('rejected_documents') }}: {{ getRejectedDocsFullText(item) }}</span>
            </v-tooltip>
          </div>
          <span v-else class="text-muted text-caption">{{ $t('no_documents_selected') }}</span>
        </template>

        <template v-slot:item.rejected_at="{ item }">
          <span v-if="item.driver_information && item.driver_information.rejected_at">
            <small>{{ item.driver_information.rejected_at | moment("LL") }}</small> -
            <small class="text-muted">{{ item.driver_information.rejected_at | moment("LT") }}</small>
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template v-slot:item.rejection_reason="{ item }">
          <v-tooltip bottom max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="text-truncate error--text font-weight-medium" style="max-width: 180px; cursor: pointer;">
                {{ getRejectionReason(item) }}
              </div>
            </template>
            <span>{{ getRejectionReason(item) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.rejected_by="{ item }">
          <span v-if="item.driver_information && item.driver_information.rejected_by_user">
            <v-chip x-small outlined color="secondary">
              {{ item.driver_information.rejected_by_user.name }}
            </v-chip>
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip v-if="tab==2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                small
                class="mr-2 primary"
                @click="$emit('view-user', item)"
              >
                <v-icon small class="mr-2">
                  mdi-card-account-mail-outline
                </v-icon>
                {{ $t("Take Action") }}
              </v-btn>
            </template>
            <span>
                {{ $t("Approve or Reject Driver") }}
            </span>
          </v-tooltip>
          <v-tooltip v-else bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mr-2" @click="$emit(userType === 'drivers' ? 'view-driver-details' : 'view-user', item)">
                mdi-eye
              </v-icon>
            </template>
            <span>
                {{ $t("View") }}
            </span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2 && tab!=3" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mr-2" @click="$emit('edit-user', item)">
                mdi-pencil
              </v-icon>
            </template>
            <span>
                {{ $t("Edit") }}
            </span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2 && tab!=3" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mr-2" @click="$emit('suspend-user', item, users.indexOf(item))">
               {{item.status_id!=1? icons.mdiAccountCheck: icons.mdiAccountOff}}
              </v-icon>
            </template>
            <span>{{item.status_id!=1? $t('Activate') : $t('Suspend') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2 && tab!=3" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="userType === 'drivers' && item.bus" v-bind="attrs" v-on="on" small class="mr-2" @click="$emit('unassign-bus', item)">
                mdi-fridge-industrial-off
              </v-icon>
              <v-icon v-else-if="userType === 'drivers'" v-bind="attrs" v-on="on" small class="mr-2" @click="$emit('assign-bus', item)">
                mdi-bus
              </v-icon>
            </template>
            <span>{{item.bus? $t('Unassign Bus') : $t('Assign Bus') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab==3" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                color="warning"
                class="ms-1"
                @click="$emit('return-to-review', item)"
              >
                <v-icon x-small class="me-1">mdi-restore</v-icon>
                إعادة للمراجعة
              </v-btn>
            </template>
            <span>إعادة الطلب إلى قائمة قيد المراجعة</span>
          </v-tooltip>
        </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  mdiAccountCheck,
  mdiAccountOff,
} from "@mdi/js";

export default {
  props: {
    users: Array,
    userType: String,
    tab: Number,
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  components: {},
  data() {
    return {
      search: "",
      failedAvatars: {},
      customersHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "phone_number", value: "phone_number" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      driverHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "phone_number", value: "phone_number" },
        { text: "Bus", value: "bus" },
        { text: "working_status", value: "working_status" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      rejectedDriverHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "phone_number", value: "phone_number" },
        { text: "rejected_documents", value: "rejected_documents" },
        { text: "rejection_reason", value: "rejection_reason" },
        { text: "rejection_date", value: "rejected_at" },
        { text: "rejected_by", value: "rejected_by" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      statuses: [
        { value: "Active", color: "success" },
        { value: "Pending", color: "warning" },
        { value: "Suspended", color: "error" },
        { value: "Under Review", color: "info" },
        { value: "Rejected", color: "error" },
      ],
      icons: {
        mdiAccountCheck,
        mdiAccountOff,
      },
    };
  },
  computed: {
    translatedHeaders() {
        let h = this.userType === "drivers"
        ? (this.tab === 3 ? this.rejectedDriverHeaders : this.driverHeaders)
        : this.customersHeaders;

        return h.map(header => {
          let label = this.$t(header.text);
          if (header.text === "phone_number") {
            label = this.$i18n.locale === "ar" ? "رقم الهاتف" : "Phone Number";
          } else if (header.text === "rejected_documents") {
            label = this.$i18n.locale === "ar" ? "المستندات المرفوضة" : "Rejected Documents";
          } else if (header.text === "rejection_date") {
            label = this.$i18n.locale === "ar" ? "تاريخ الرفض" : "Rejection Date";
          } else if (header.text === "rejection_reason") {
            label = this.$i18n.locale === "ar" ? "سبب الرفض" : "Rejection Reason";
          } else if (header.text === "rejected_by") {
            label = this.$i18n.locale === "ar" ? "الأدمن الرافض" : "Rejected By";
          }
          return {
            ...header,
            text: label
          };
        });
    },
    footerProps() {
        return {
            itemsPerPageText: this.$t("rows_per_page"),
        }
    }
  },
  methods: {
    getStatusColor(status)
    {
      return this.statuses[status-1] ? this.statuses[status-1].color : 'grey';
    },
    getStatusValue(status)
    {
      return this.statuses[status-1] ? this.statuses[status-1].value : 'Unknown';
    },
    getRejectionReason(item) {
      if (item.driver_information) {
        return item.driver_information.rejection_reason || item.driver_information.response || "لا يوجد سبب محدد";
      }
      return "لا يوجد سبب محدد";
    },
    getRejectedDocs(item) {
      if (!item.driver_information || !item.driver_information.rejected_documents) return [];
      const docs = item.driver_information.rejected_documents;
      if (!Array.isArray(docs)) return [];
      return docs.map(d => (typeof d === 'object' && d !== null ? d.name || d.key || String(d) : String(d)));
    },
    getRejectedDocsPreview(item) {
      const docs = this.getRejectedDocs(item);
      return docs.slice(0, 2);
    },
    getRejectedDocsMoreCount(item) {
      const docs = this.getRejectedDocs(item);
      return docs.length > 2 ? docs.length - 2 : 0;
    },
    getRejectedDocsFullText(item) {
      return this.getRejectedDocs(item).join('، ');
    },
    getBusAssignmentColor(bus)
    {
      return bus != null? 'info': 'error';
    },
    getPhoneNumber(item) {
      if (item.tel_number) return item.tel_number;
      if (item.driver_information && item.driver_information.phone_number) {
        return item.driver_information.phone_number;
      }
      return "-";
    },
    getAvatarUrl(item) {
      if (item.avatar_url) return item.avatar_url;
      if (item.avatar) {
        if (item.avatar.startsWith('http://') || item.avatar.startsWith('https://')) {
          return item.avatar;
        }
        return '/backend' + (item.avatar.startsWith('/') ? '' : '/') + item.avatar;
      }
      return null;
    },
    getInitials(name) {
      if (!name) return "U";
      return name.charAt(0).toUpperCase();
    },
    onSearchInput(val) {
      this.$emit("search-change", val);
    },
    isDriverItem(item) {
      return this.userType === 'drivers' || (item && item.role === 2);
    },
    hasRating(item) {
      return item && item.rating_average !== null && item.rating_average !== undefined && Number(item.ratings_count) > 0;
    },
    formatRating(item) {
      if (!this.hasRating(item)) {
        return '--';
      }
      const avg = Number(item.rating_average);
      return isNaN(avg) ? '--' : avg.toFixed(1);
    },
    getRatingTooltip(item) {
      if (this.hasRating(item)) {
        return `${this.formatRating(item)} ${this.$t('rating_from')} ${item.ratings_count} ${this.$t('ratings_unit')}`;
      }
      return this.$t('no_ratings_yet');
    },
    hasMonthlyDistance(item) {
      return item && item.monthly_distance_km !== null && item.monthly_distance_km !== undefined;
    },
    formatMonthlyDistance(item) {
      if (!this.hasMonthlyDistance(item)) {
        return '';
      }
      return `${item.monthly_distance_km} ${this.$t('km') || 'كم'}`;
    }
  },
};
</script>
<style lang="scss">
.theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled):hover{
  cursor: pointer;
  background: rgba($primary-shade--light, 0.15) !important;
}
</style>
